import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Download, Camera, AlertCircle, Trash2, X, Check, ZoomIn } from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { safeRemoveNode } from '../../../utils/dom';
import api from '../../../api/nodeBackApi';
import { getObraByFeedID } from '../../../obras/api/obras.api';
import { getObraTimeZone } from '../../../videoEditor/utils/obraTimeZone';
import { formatTime24 } from '../../../utils/formatTime24';

const ImageGallery = ({ selectedDate, user }) => {
  const [images, setImages] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isZipping, setIsZipping] = useState(false);
  const [failedImages, setFailedImages] = useState(new Set());
  const [zipProgress, setZipProgress] = useState({
    processed: 0,
    total: 0,
    percent: 0,
    label: ''
  });
  const [zipError, setZipError] = useState(null);
  const [zipCancelled, setZipCancelled] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteAllModalOpen, setDeleteAllModalOpen] = useState(false);
  const [confirmationTextAll, setConfirmationTextAll] = useState('');
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [deleteMultipleModalOpen, setDeleteMultipleModalOpen] = useState(false);
  const [confirmationTextMultiple, setConfirmationTextMultiple] = useState('');
  const [isDeletingMultiple, setIsDeletingMultiple] = useState(false);

  const [previewImage, setPreviewImage] = useState(null);

  // Camera times are shown in the obra's zone; fails open to the browser zone when the obra has no location.
  const [obraTimeZone, setObraTimeZone] = useState(null);
  useEffect(() => {
    if (!user?.feedID) return undefined;
    let cancelled = false;
    getObraByFeedID(user.feedID)
      .then((obra) => {
        if (!cancelled) setObraTimeZone(getObraTimeZone(obra));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.feedID]);

  const abortControllersRef = useRef([]);

  useEffect(() => {
    if (!previewImage) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setPreviewImage(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [previewImage]);

  const [historyStartDate, setHistoryStartDate] = useState(() =>
      selectedDate ? new Date(selectedDate) : new Date()
  );

  const [zipMode, setZipMode] = useState('day');

  const apiKey = "HWN2M2mcbZoyFDTfpaskQV9TBDk0Ir8bu4XWDxJQiBE";
  const apiBaseUrl = process.env.REACT_APP_BACKEND_URL;
  const s3ApiBaseUrl = process.env.REACT_APP_WEBBACK_URL || 'https://webback.lisual.com'; 
  //const s3ApiBaseUrl = "http://localhost:8080";

  const fallbackImageUrl =
      'data:image/svg+xml;base64,' +
      btoa(`
      <svg width="300" height="200" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#f3f4f6"/>
        <text x="50%" y="50%" font-family="Arial" font-size="16" fill="#6b7280" text-anchor="middle">
          Error al cargar imagen
        </text>
      </svg>
    `);

  const CACHE_DURATION = 5 * 60 * 1000;

  const getAuthToken = () => {
    return localStorage.getItem('authLisualToken');
  };

  const getUserRoleFromToken = () => {
    try {
      const token = getAuthToken();
      if (!token) return null;

      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const payload = JSON.parse(atob(parts[1]));
      return payload.role || null;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  const isUserResponsable = () => {
    const role = getUserRoleFromToken();
    return role === 'responsibleUser' || role === 'admin';
  };

  const formatDate = (date) => {
    return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
  };

  const getDateKey = (date) => {
    return date.toISOString().slice(0, 10);
  };

  const sanitizeFileName = (name) => {
    return name.replace(/[:\\/*?|<>\s]/g, '-');
  };

  const formatDateInput = (date) => {
    if (!date) return '';

    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');

    return `${y}-${m}-${d}`;
  };

  const parseDateInputToUTCDate = (value) => {
    if (!value) return null;
    return new Date(`${value}T00:00:00.000Z`);
  };

  const formatDateToSpanish = (date) => {
    const days = [
      'Domingo',
      'Lunes',
      'Martes',
      'Miércoles',
      'Jueves',
      'Viernes',
      'Sábado'
    ];

    const months = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre'
    ];

    return `${days[date.getUTCDay()]}, ${date.getUTCDate()} de ${months[date.getUTCMonth()]} de ${date.getUTCFullYear()}`;
  };

  const handleApiError = (status) => {
    const errorMessages = {
      400: "Parámetros inválidos.",
      401: "No autorizado.",
      403: "Sin permisos.",
      404: "No encontrado.",
      500: "Error del servidor.",
      503: "Servicio no disponible.",
      504: "Timeout."
    };

    return errorMessages[status] || `Error: ${status}`;
  };

  const getCachedData = (key) => {
    try {
      const cachedString = localStorage.getItem(key);

      if (!cachedString) return null;

      const cached = JSON.parse(cachedString);

      if (Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.data;
      }

      return null;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const setCachedData = (key, data) => {
    try {
      localStorage.setItem(
          key,
          JSON.stringify({
            data,
            timestamp: Date.now()
          })
      );
    } catch (e) {
      console.error(e);
    }
  };

  const parseTimestamps = (data) => {
    if (!data) return [];

    if (Array.isArray(data)) {
      if (data.length > 0 && Array.isArray(data[0])) {
        return data.flat();
      }

      return data;
    }

    if (data.frames && data.frames.Frames && Array.isArray(data.frames.Frames)) {
      return data.frames.Frames;
    }

    if (data.frames && Array.isArray(data.frames)) {
      return data.frames;
    }

    if (data.timestamps && Array.isArray(data.timestamps)) {
      return data.timestamps;
    }

    if (data.Frames && Array.isArray(data.Frames)) {
      return data.Frames;
    }

    return [];
  };

  const formatImageTime = (timestamp) => {
    if (!timestamp) return '--:--:--';

    try {
      return formatTime24(timestamp, { seconds: true, timeZone: obraTimeZone || undefined });
    } catch {
      return '--:--:--';
    }
  };

  const formatDateTimeForDisplay = (timestamp) => {
    if (!timestamp) return '';

    try {
      const date = new Date(timestamp);
      const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
      const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

      // Calendar date as seen in the obra's zone (the UTC date can be a day off near midnight).
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-US', { timeZone: obraTimeZone || undefined, year: 'numeric', month: 'numeric', day: 'numeric' })
          .formatToParts(date)
          .map((p) => [p.type, Number(p.value)])
      );
      const dayName = days[new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay()];
      const day = parts.day;
      const monthName = months[parts.month - 1];
      const year = parts.year;
      const time = formatImageTime(timestamp);

      return `${dayName}, ${day} de ${monthName} de ${year} a las ${time}`;
    } catch {
      return timestamp;
    }
  };

  const openDeleteModal = (e, image) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setImageToDelete(image);
    setConfirmationText('');
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setImageToDelete(null);
    setConfirmationText('');
  };

  const deleteImage = async () => {
    if (!imageToDelete) return;

    const dateStr = imageToDelete.timestamp.split('T')[0];
    const expectedText = `confirmo borrar la imagen del ${dateStr}`;

    if (confirmationText !== expectedText) {
      alert('Texto de confirmación incorrecto');
      return;
    }

    try {
      setIsDeleting(true);

      const authToken = getAuthToken();
      const deleteUrl = `${s3ApiBaseUrl}/api/s3-images/${user.feedID}/image?fileName=${encodeURIComponent(imageToDelete.url)}`;

      await api.delete(deleteUrl, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      setImages(prevImages => prevImages.filter(img => img.url !== imageToDelete.url));
      closeDeleteModal();
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Error al borrar la imagen. Intenta de nuevo.');
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteAllModal = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setConfirmationTextAll('');
    setDeleteAllModalOpen(true);
  };

  const closeDeleteAllModal = () => {
    setDeleteAllModalOpen(false);
    setConfirmationTextAll('');
  };

  const deleteAllImages = async () => {
    const dateStr = getDateKey(selectedDate);
    const expectedText = `confirmo borrar todas las imágenes del ${dateStr}`;

    if (confirmationTextAll !== expectedText) {
      alert('Texto de confirmación incorrecto');
      return;
    }

    try {
      setIsDeletingAll(true);

      const authToken = getAuthToken();
      const deleteUrl = `${s3ApiBaseUrl}/api/s3-images/${user.feedID}/images/date/${dateStr}`;

      await api.delete(deleteUrl, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      setImages([]);
      closeDeleteAllModal();
    } catch (error) {
      console.error('Error deleting all images:', error);
      alert('Error al borrar las imágenes. Intenta de nuevo.');
    } finally {
      setIsDeletingAll(false);
    }
  };

  const toggleMultiSelectMode = () => {
    setMultiSelectMode(!multiSelectMode);
    setSelectedImages(new Set());
  };

  const toggleImageSelection = (imageUrl) => {
    setSelectedImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageUrl)) {
        newSet.delete(imageUrl);
      } else {
        newSet.add(imageUrl);
      }
      return newSet;
    });
  };

  const selectAllImages = () => {
    const allImageUrls = new Set(
        images.filter(img => !failedImages.has(img.url)).map(img => img.url)
    );
    setSelectedImages(allImageUrls);
  };

  const deselectAllImages = () => {
    setSelectedImages(new Set());
  };

  const openDeleteMultipleModal = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setConfirmationTextMultiple('');
    setDeleteMultipleModalOpen(true);
  };

  const closeDeleteMultipleModal = () => {
    setDeleteMultipleModalOpen(false);
    setConfirmationTextMultiple('');
  };

  const deleteSelectedImages = async () => {
    const selectedCount = selectedImages.size;
    const expectedText = `confirmo borrar ${selectedCount} imágenes`;

    if (confirmationTextMultiple !== expectedText) {
      alert('Texto de confirmación incorrecto');
      return;
    }

    try {
      setIsDeletingMultiple(true);
      const authToken = getAuthToken();
      const imagesToDelete = Array.from(selectedImages);

      for (const imageUrl of imagesToDelete) {
        try {
          const deleteUrl = `${s3ApiBaseUrl}/api/s3-images/${user.feedID}/image?fileName=${encodeURIComponent(imageUrl)}`;
          await api.delete(deleteUrl, {
            headers: {
              Authorization: `Bearer ${authToken}`
            }
          });
        } catch (error) {
          console.error(`Error deleting image ${imageUrl}:`, error);
        }
      }

      setImages(prevImages =>
          prevImages.filter(img => !selectedImages.has(img.url))
      );
      setSelectedImages(new Set());
      setMultiSelectMode(false);
      closeDeleteMultipleModal();
    } catch (error) {
      console.error('Error deleting selected images:', error);
      alert('Error al borrar las imágenes. Intenta de nuevo.');
    } finally {
      setIsDeletingMultiple(false);
    }
  };

  const fetchImages = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const start = new Date(selectedDate);
      start.setUTCHours(0, 0, 0, 0);

      const end = new Date(selectedDate);
      end.setUTCHours(23, 59, 59, 999);

      const cacheKey = `${user.feedID}-${getDateKey(start)}`;

      const cachedImages = getCachedData(cacheKey);

      if (cachedImages) {
        setImages(cachedImages);
      }

      let imageUrls = [];

      // Always assume FTPS3 logic
      if (!user?.feedID) {
        setError("Missing feedID.");
        return;
      }

      const authToken = getAuthToken();

      if (!authToken) {
        setError("No auth token.");
        return;
      }

      const formattedDate = getDateKey(selectedDate);

      const s3ApiUrl =
          `${s3ApiBaseUrl}/api/s3-images/${user.feedID}/daily?date=${formattedDate}`;

      const response = await api.get(s3ApiUrl, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      const data = response.data;

      const s3ImageUrls = parseTimestamps(data);

      imageUrls = s3ImageUrls.map((item) => { // item is { url: string, date: string }
        const timestamp = typeof item === 'string' ? item : item.date;

        // Filtrar si la API reporta 0 bytes directamente
        if (typeof item === 'object' && (item.size === 0 || item.byteSize === 0)) {
          return null;
        }

        return {
          url: typeof item === 'string' ? item : item.url,
          timestamp: timestamp, // This is already an ISO string from the backend
        };
      }).filter(Boolean);

      setCachedData(cacheKey, imageUrls);
      setImages(imageUrls);

    } catch (error) {
      console.error(error);

      if (error.response) {
        setError(handleApiError(error.response.status));
      } else {
        setError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate, user]);

  useEffect(() => {
    if (!selectedDate) return;
    setFailedImages(new Set()); // Limpiar errores al cambiar de fecha
    fetchImages();
  }, [fetchImages]);

  const downloadImage = async (e, imageUrl, timestamp) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      const requestConfig = {
        responseType: 'blob',
        headers: {}
      };

      const authToken = getAuthToken();
      if (authToken) {
        requestConfig.headers.Authorization = `Bearer ${authToken}`;
      }

      const response = await api.get(imageUrl, requestConfig);
      const blob = response.data; // Axios devuelve el blob en .data
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.style.display = 'none';
      a.href = url;
      
      // IMPORTANTE: Sanitizar el nombre del archivo. 
      // Los timestamps ISO tienen ":" que son caracteres inválidos en Windows/macOS para nombres de archivos.
      const safeName = sanitizeFileName(`image-${timestamp || Date.now()}`);
      a.download = `${safeName}.jpg`;

      document.body.appendChild(a);

      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        safeRemoveNode(a, document.body);
      }, 1000);

    } catch (e) {
      console.error('Error downloading image:', e);
      // Fallback: Si falla la descarga por Blob (ej. por CORS), intentamos abrir la imagen en una pestaña nueva
      // para que el usuario pueda guardarla manualmente.
      window.open(imageUrl, '_blank');
    }
  };

  if (error) {
    return (
        <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />

          <div className="text-red-500 text-center">
            <p className="font-semibold mb-2">Error</p>
            <p>{error}</p>
          </div>
        </div>
    );
  }

  if (isLoading) {
    return (
        <div className="p-8 text-center">
          Cargando imágenes...
        </div>
    );
  }

  if (images.length === 0) {
    return (
        <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg">
          <Camera className="w-16 h-16 text-gray-400 mb-4" />

          <h3 className="text-xl font-semibold text-gray-600 mb-2">
            No hay fotos disponibles
          </h3>

          <p className="text-gray-500 text-center">
            No se encontraron fotos para el día {formatDateToSpanish(selectedDate)}
          </p>
        </div>
    );
  }

  return (
      <div className="w-full max-w-full overflow-hidden">

        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
          <div className="text-gray-600 text-sm sm:text-base">
            Se encontraron {images.filter(img => !failedImages.has(img.url)).length} imágenes
            {multiSelectMode && selectedImages.size > 0 && (
                <span className="ml-2 text-blue-600 font-medium">
                  ({selectedImages.size} seleccionada{selectedImages.size !== 1 ? 's' : ''})
                </span>
            )}
          </div>
          <button
              onClick={toggleMultiSelectMode}
              className={`px-4 py-2 rounded-lg transition font-medium text-sm ${
                  multiSelectMode
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            {multiSelectMode ? 'Salir de selección' : 'Seleccionar múltiples'}
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4 mb-6">
          {images.filter(image => !failedImages.has(image.url)).map((image, index) => (
              <div
                  key={index}
                  className={`relative group bg-gray-100 rounded-lg sm:rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 ${
                      multiSelectMode && selectedImages.has(image.url) ? 'ring-2 ring-blue-500' : ''
                  }`}
                  onClick={() => {
                    if (multiSelectMode) {
                      toggleImageSelection(image.url);
                    }
                  }}
              >
                {multiSelectMode && (
                    <div className="absolute top-2 right-2 z-20">
                      <div
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center transition ${
                              selectedImages.has(image.url)
                                  ? 'bg-blue-600 border-blue-600'
                                  : 'bg-white border-gray-300 hover:border-blue-500'
                          }`}
                      >
                        {selectedImages.has(image.url) && (
                            <Check className="w-4 h-4 text-white" />
                        )}
                      </div>
                    </div>
                )}

                <div className={`absolute inset-0 flex items-center justify-center gap-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 z-10 ${
                    multiSelectMode ? 'group-hover:opacity-0' : 'group-hover:opacity-100'
                } ${
                    // Touch devices have no hover: keep the actions visible as a compact corner cluster
                    multiSelectMode
                        ? ''
                        : '[@media(hover:none)]:opacity-100 [@media(hover:none)]:bg-transparent [@media(hover:none)]:items-end [@media(hover:none)]:justify-end [@media(hover:none)]:gap-2 [@media(hover:none)]:p-2 [@media(hover:none)]:pointer-events-none'
                }`}>
                  <ZoomIn
                      aria-label="Ver imagen grande"
                      className="text-white w-8 h-8 sm:w-12 sm:h-12 cursor-pointer hover:scale-110 transition-transform duration-200 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:w-11 [@media(hover:none)]:h-11 [@media(hover:none)]:p-2.5 [@media(hover:none)]:bg-black/50 [@media(hover:none)]:rounded-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewImage(image);
                      }}
                  />
                  <Download
                      className="text-white w-8 h-8 sm:w-12 sm:h-12 cursor-pointer hover:scale-110 transition-transform duration-200 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:w-11 [@media(hover:none)]:h-11 [@media(hover:none)]:p-2.5 [@media(hover:none)]:bg-black/50 [@media(hover:none)]:rounded-full"
                      onClick={(e) => downloadImage(e, image.url, image.timestamp)}
                  />
                  <Trash2
                      className="text-red-400 w-8 h-8 sm:w-12 sm:h-12 cursor-pointer hover:scale-110 hover:text-red-300 transition-transform duration-200 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:w-11 [@media(hover:none)]:h-11 [@media(hover:none)]:p-2.5 [@media(hover:none)]:bg-black/50 [@media(hover:none)]:rounded-full"
                      onClick={(e) => openDeleteModal(e, image)}
                  />
                </div>

                <div className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-white bg-opacity-70 p-1 rounded text-xs z-20">
                  {formatImageTime(image.timestamp)}
                </div>

                <div className="relative aspect-video">
                  <img
                      src={image.url}
                      alt={`Image ${index + 1}`}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                      onError={() => {
                        // Si la imagen falla (0 bits o corrupta), la agregamos a failedImages para ocultar el casillero
                        setFailedImages(prev => new Set(prev).add(image.url));
                      }}
                  />
                </div>
              </div>
          ))}
        </div>

        <div className="border-t pt-6 space-y-4">
          {multiSelectMode && (
              <div className="flex gap-2">
                <button
                    onClick={selectAllImages}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition font-medium text-sm"
                >
                  Seleccionar todo
                </button>
                <button
                    onClick={deselectAllImages}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition font-medium text-sm"
                >
                  Deseleccionar todo
                </button>
                {selectedImages.size > 0 && (
                    <button
                        onClick={openDeleteMultipleModal}
                        className="flex-1 px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition font-medium text-sm"
                    >
                      Eliminar {selectedImages.size} imagen{selectedImages.size !== 1 ? 's' : ''}
                    </button>
                )}
              </div>
          )}
          <button
              onClick={openDeleteAllModal}
              className="w-full px-4 py-3 text-white bg-red-600 hover:bg-red-700 rounded-lg transition font-medium"
          >
            Borrar todas las imágenes para este día
          </button>
        </div>

        {previewImage && (
            <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4"
                onClick={() => setPreviewImage(null)}
                role="dialog"
                aria-modal="true"
            >
              <button
                  type="button"
                  aria-label="Cerrar"
                  className="absolute top-4 right-4 text-white hover:text-gray-300"
                  onClick={() => setPreviewImage(null)}
              >
                <X className="w-8 h-8" />
              </button>
              <div className="max-w-full max-h-full flex flex-col items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <img
                    src={previewImage.url}
                    alt={formatImageTime(previewImage.timestamp) || 'Imagen'}
                    className="max-w-full max-h-[85vh] object-contain rounded-lg"
                />
                {previewImage.timestamp && (
                    <span className="text-white text-sm">{formatImageTime(previewImage.timestamp)}</span>
                )}
              </div>
            </div>
        )}

        {deleteModalOpen && imageToDelete && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Borrar imagen</h2>
                  <button
                      onClick={closeDeleteModal}
                      className="text-gray-400 hover:text-gray-600 transition"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="p-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>ℹ️ Aviso:</strong> Solo los usuarios con rol de responsable pueden eliminar imágenes.
                    </p>
                  </div>

                  <p className="text-gray-700 mb-4">
                    ¿Estás seguro de que deseas borrar la imagen del <strong>{formatDateTimeForDisplay(imageToDelete.timestamp)}</strong>?
                  </p>

                  <p className="text-sm text-gray-600 mb-4">
                    Para confirmar, escribe el siguiente texto:
                  </p>

                  <div className="bg-gray-100 p-3 rounded mb-4 border border-gray-300">
                    <p className="font-mono text-sm text-gray-700">
                      confirmo borrar la imagen del {imageToDelete.timestamp.split('T')[0]}
                    </p>
                  </div>

                  <input
                      type="text"
                      value={confirmationText}
                      onChange={(e) => setConfirmationText(e.target.value)}
                      placeholder="Escribe el texto de confirmación"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
                      disabled={isDeleting}
                  />

                  <div className="flex gap-3">
                    <button
                        onClick={closeDeleteModal}
                        disabled={isDeleting}
                        className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancelar
                    </button>
                    <button
                        onClick={deleteImage}
                        disabled={isDeleting || !isUserResponsable() || confirmationText !== `confirmo borrar la imagen del ${imageToDelete.timestamp.split('T')[0]}`}
                        className="flex-1 px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                        title={!isUserResponsable() ? 'Solo responsables pueden eliminar imágenes' : ''}
                    >
                      {isDeleting ? 'Borrando...' : 'Borrar'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
        )}

        {deleteMultipleModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
                  <h2 className="text-lg font-semibold text-gray-900">Eliminar imágenes seleccionadas</h2>
                  <button
                      onClick={closeDeleteMultipleModal}
                      className="text-gray-400 hover:text-gray-600 transition"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="p-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>ℹ️ Aviso:</strong> Solo los usuarios con rol de responsable pueden eliminar imágenes.
                    </p>
                  </div>

                  <p className="text-gray-700 mb-4">
                    ¿Estás seguro de que deseas borrar <strong>{selectedImages.size} imágenes</strong>?
                  </p>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 max-h-64 overflow-y-auto">
                    <p className="text-sm font-medium text-gray-700 mb-3">Imágenes a eliminar:</p>
                    <ul className="space-y-2">
                      {images.filter(img => selectedImages.has(img.url)).map((image, index) => (
                          <li key={index} className="text-sm text-gray-600 flex items-start gap-2">
                            <span className="text-gray-400 flex-shrink-0">•</span>
                            <span>{formatDateTimeForDisplay(image.timestamp)}</span>
                          </li>
                      ))}
                    </ul>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">
                    Para confirmar, escribe el siguiente texto:
                  </p>

                  <div className="bg-gray-100 p-3 rounded mb-4 border border-gray-300">
                    <p className="font-mono text-sm text-gray-700">
                      confirmo borrar {selectedImages.size} imágenes
                    </p>
                  </div>

                  <input
                      type="text"
                      value={confirmationTextMultiple}
                      onChange={(e) => setConfirmationTextMultiple(e.target.value)}
                      placeholder="Escribe el texto de confirmación"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
                      disabled={isDeletingMultiple}
                  />

                  <div className="flex gap-3">
                    <button
                        onClick={closeDeleteMultipleModal}
                        disabled={isDeletingMultiple}
                        className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancelar
                    </button>
                    <button
                        onClick={deleteSelectedImages}
                        disabled={isDeletingMultiple || !isUserResponsable() || confirmationTextMultiple !== `confirmo borrar ${selectedImages.size} imágenes`}
                        className="flex-1 px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                        title={!isUserResponsable() ? 'Solo responsables pueden eliminar imágenes' : ''}
                    >
                      {isDeletingMultiple ? 'Borrando...' : 'Eliminar'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
        )}

        {deleteAllModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
                  <h2 className="text-lg font-semibold text-gray-900">Borrar todas las imágenes</h2>
                  <button
                      onClick={closeDeleteAllModal}
                      className="text-gray-400 hover:text-gray-600 transition"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="p-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>ℹ️ Aviso:</strong> Solo los usuarios con rol de responsable pueden eliminar imágenes.
                    </p>
                  </div>

                  <p className="text-gray-700 mb-4">
                    ¿Estás seguro de que deseas borrar <strong>{images.filter(img => !failedImages.has(img.url)).length} imágenes</strong> del {formatDateToSpanish(selectedDate)}?
                  </p>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 max-h-64 overflow-y-auto">
                    <p className="text-sm font-medium text-gray-700 mb-3">Imágenes a eliminar:</p>
                    <ul className="space-y-2">
                      {images.filter(img => !failedImages.has(img.url)).map((image, index) => (
                          <li key={index} className="text-sm text-gray-600 flex items-start gap-2">
                            <span className="text-gray-400 flex-shrink-0">•</span>
                            <span>{formatDateTimeForDisplay(image.timestamp)}</span>
                          </li>
                      ))}
                    </ul>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">
                    Para confirmar, escribe el siguiente texto:
                  </p>

                  <div className="bg-gray-100 p-3 rounded mb-4 border border-gray-300">
                    <p className="font-mono text-sm text-gray-700">
                      confirmo borrar todas las imágenes del {getDateKey(selectedDate)}
                    </p>
                  </div>

                  <input
                      type="text"
                      value={confirmationTextAll}
                      onChange={(e) => setConfirmationTextAll(e.target.value)}
                      placeholder="Escribe el texto de confirmación"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
                      disabled={isDeletingAll}
                  />

                  <div className="flex gap-3">
                    <button
                        onClick={closeDeleteAllModal}
                        disabled={isDeletingAll}
                        className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancelar
                    </button>
                    <button
                        onClick={deleteAllImages}
                        disabled={isDeletingAll || !isUserResponsable() || confirmationTextAll !== `confirmo borrar todas las imágenes del ${getDateKey(selectedDate)}`}
                        className="flex-1 px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                        title={!isUserResponsable() ? 'Solo responsables pueden eliminar imágenes' : ''}
                    >
                      {isDeletingAll ? 'Borrando...' : 'Borrar todas'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
        )}
      </div>
  );
};

export default ImageGallery;